import { useState, ReactNode } from 'react'
import FilterPanelContext from './FilterPanelContext'
import { FilterSelect } from './FilterSelect'
import { FilterInput } from './FilterInput'
import { FilterRadioGroup } from './FilterRadioGroup'
import { FilterSearchableSelect } from './FilterSearchableSelect'
import AppButton from '../../atoms/AppButton/AppButton'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Props {
    children: ReactNode
    onApply: (values: Record<string, unknown>) => void
    onReset: () => void
    onClose: () => void
    title?: string
    initialValues?: Record<string, unknown>
}

// Compound component interface — sin React.FC (punto 4)
interface FilterPanelComponent {
    (props: Props): ReactNode
    Select: typeof FilterSelect
    Input: typeof FilterInput
    RadioGroup: typeof FilterRadioGroup
    SearchableSelect: typeof FilterSearchableSelect
}

// ─── Main component ───────────────────────────────────────────────────────────

const FilterPanel: FilterPanelComponent = ({
    children,
    onApply,
    onReset,
    onClose,
    title = 'Filtros',
    initialValues = {},
}) => {
    // The panel owns its working copy of the filter values. It is seeded once
    // from initialValues; the parent re-seeds it by remounting the panel (key),
    // so no syncing effect is needed (React 19 / fewer effects).
    const [values, setValues] = useState<Record<string, unknown>>(initialValues)

    const setValue = (name: string, value: unknown) =>
        setValues((prev) => ({ ...prev, [name]: value }))

    const clearValue = (name: string) =>
        setValues((prev) => {
            const next = { ...prev }
            delete next[name]
            return next
        })

    const handleReset = () => {
        setValues({})
        onReset()
    }

    const handleApply = () => onApply(values)

    return (
        <FilterPanelContext value={{ values, setValue, clearValue }}>
            <div className="flex flex-col w-full bg-white rounded-[16px] shadow-[0px_6px_12px_0px_rgba(148,163,184,0.15)]">

                {/* Header */}
                <div className="flex items-center justify-between p-[16px] bg-[#F1F5F9] rounded-t-[16px]">
                    <h2 className="text-[18px] leading-[22px] font-medium text-[#3E5060]">{title}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-[#3E5060] hover:text-gray-900 transition-colors"
                        aria-label="Cerrar"
                    >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="flex flex-col gap-[16px] p-[16px]">
                    {children}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-3 p-[16px] border-t border-[#CBD5E1] bg-white rounded-b-[16px]">
                    <AppButton
                        title="Resetear"
                        state="ghost_secondary"
                        onClick={handleReset}
                        className="flex-1"
                    />
                    <AppButton
                        title="Aplicar filtro"
                        state="primary"
                        onClick={handleApply}
                        className="flex-1"
                    />
                </div>
            </div>
        </FilterPanelContext>
    )
}

FilterPanel.Select = FilterSelect
FilterPanel.Input = FilterInput
FilterPanel.RadioGroup = FilterRadioGroup
FilterPanel.SearchableSelect = FilterSearchableSelect

export default FilterPanel
